import type {
	ColumnSizingState,
	OnChangeFn,
	VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";

/** Default pixel widths for resizable columns. Narrative is stretch (1fr) — its value is ignored visually. */
export const DEFAULT_COLUMN_SIZING: ColumnSizingState = {
	date: 100,
	matter: 280,
	codes: 90,
	narrative: 600,
	units: 140,
};

export const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
	app: true,
	date: true,
	codes: true,
};

type PersistedState = {
	columnSizing: ColumnSizingState;
	columnVisibility: VisibilityState;
};

function hydrate(storageKey: string): PersistedState {
	const fallback: PersistedState = {
		columnSizing: { ...DEFAULT_COLUMN_SIZING },
		columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY },
	};
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return fallback;
		const parsed = JSON.parse(raw);
		const sizing =
			parsed?.columnSizing && typeof parsed.columnSizing === "object"
				? parsed.columnSizing
				: null;
		const visibility =
			parsed?.columnVisibility && typeof parsed.columnVisibility === "object"
				? parsed.columnVisibility
				: null;
		return {
			columnSizing: sizing
				? { ...DEFAULT_COLUMN_SIZING, ...sizing }
				: { ...DEFAULT_COLUMN_SIZING },
			columnVisibility: visibility
				? { ...DEFAULT_COLUMN_VISIBILITY, ...visibility }
				: { ...DEFAULT_COLUMN_VISIBILITY },
		};
	} catch {
		return fallback;
	}
}

/**
 * Persistent column sizing + visibility for an EntriesTable instance.
 * One localStorage key per table; debounced writes (250ms) to avoid
 * thrashing storage during a drag (`columnResizeMode: "onChange"` fires
 * every frame).
 */
export function useEntriesTableState(storageKey: string) {
	const initialRef = useRef<PersistedState | null>(null);
	if (initialRef.current === null) {
		initialRef.current = hydrate(storageKey);
	}

	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
		initialRef.current.columnSizing,
	);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
		initialRef.current.columnVisibility,
	);

	const timeoutRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		if (timeoutRef.current !== undefined) {
			window.clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = window.setTimeout(() => {
			try {
				localStorage.setItem(
					storageKey,
					JSON.stringify({ columnSizing, columnVisibility }),
				);
			} catch {
				// localStorage may be unavailable (private mode, quota); swallow.
			}
		}, 250);
		return () => {
			if (timeoutRef.current !== undefined) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, [storageKey, columnSizing, columnVisibility]);

	const onColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
		(updater) => {
			setColumnSizing((prev) =>
				typeof updater === "function" ? updater(prev) : updater,
			);
		},
		[],
	);

	const onColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
		(updater) => {
			setColumnVisibility((prev) =>
				typeof updater === "function" ? updater(prev) : updater,
			);
		},
		[],
	);

	return {
		columnSizing,
		columnVisibility,
		onColumnSizingChange,
		onColumnVisibilityChange,
	};
}
